import { produce } from 'immer';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Grid, HStack, IconButton, PlusOutline, Spacer, TrashOutline, XOutline } from '@bonusly/design-system';

import { Button } from '../../design-system/button';
import { Dropdown, DropdownInput, DropdownMenu, DropdownOption } from '../../design-system/dropdown';
import { Text } from '../../design-system/typography';
import { CustomPropertyUserGroup, useCustomPropertyUserGroups } from '../../queries/groups';
import { Conceal } from '../display/conceal';
import { Reveal } from '../display/reveal';
import { AttributeSearch } from '../form/attribute-search';
import { FormErrorMessage } from '../form/form-error-message';

export type AudienceCondition = { attr: string | undefined; value: string | undefined };

type AudienceSelectorProps = {
	error?: string;
	isDisabled?: boolean;
	onChange: (value: AudienceCondition[]) => void;
	value: AudienceCondition[];
	translationNamespace?: string;
	translationKeyPrefix?: string;
};

const placeholderContext = (attr: string) => {
	if (['department', 'email', 'location', 'role'].includes(attr)) {
		return attr;
	}

	return 'other';
};

export const AudienceSelector: FC<AudienceSelectorProps> = ({
	error,
	isDisabled = false,
	onChange,
	value,
	translationNamespace = 'awards',
	translationKeyPrefix = 'form',
}) => {
	const { data } = useCustomPropertyUserGroups();
	const { t } = useTranslation(translationNamespace, { keyPrefix: translationKeyPrefix });

	const options = useMemo(() => {
		return Object.values(data ?? {})
			.flat()
			.reduce<Record<string, CustomPropertyUserGroup[]>>((groups, condition) => {
				if (!(condition.type in groups)) {
					return {
						...groups,
						[condition.type]: [condition],
					};
				}
				return {
					...groups,
					[condition.type]: [...groups[condition.type], condition],
				};
			}, {});
	}, [data]);

	// Create a top level audience selector set, which will be used to determine if the "Add Audience" button should be displayed.
	// If the user happens to have selected all available options, then the "Add Audience" button should be hidden.
	const availableOptions = useMemo(() => {
		return produce(options, (draft) => {
			Object.keys(options).forEach((attr) => {
				draft[attr] = draft[attr].filter((option) => !value.some((item) => item.attr === attr && item.value === option.key));
			});
		});
	}, [options, value]);

	const handleRemove = (index: number) => {
		if (value.length === 1) {
			onChange([{ attr: undefined, value: undefined }]);
		} else {
			onChange(value.filter((_, i) => i !== index));
		}
	};

	const handleClear = () => {
		onChange([{ attr: undefined, value: undefined }]);
	};

	const handleAdd = () => {
		onChange([...value, { attr: undefined, value: undefined }]);
	};

	const handleChangeAttr = (index: number, attr: string) => {
		const newValue = produce(value, (draft) => {
			draft[index] = {
				attr,
				value: undefined,
			};
		});

		onChange(newValue);
	};

	const handleChangeValue = (index: number, val: string) => {
		const newValue = produce(value, (draft) => {
			draft[index].value = val;
		});

		onChange(newValue);
	};

	return (
		<Box aria-disabled={isDisabled} pointerEvents={isDisabled ? 'none' : undefined} opacity={isDisabled ? 0.7 : undefined}>
			<Grid rowGap={2} templateColumns="1fr 7rem 1fr 3.5rem">
				{value.map((condition, index) => {
					// Create a list of all of the selected options other than this one being rendered
					const alreadySelectedOptions = produce(value, (draft) => {
						draft.splice(index, 1);
					}).filter((item) => item.attr && item.value);

					// Build a set of options that can be selected from specific to this set
					const filteredOptions = produce(options, (draft) => {
						Object.keys(options).forEach((attr) => {
							draft[attr] = draft[attr].filter((option) => !alreadySelectedOptions.some((o) => o.attr === attr && o.value === option.key));
						});

						Object.keys(options).forEach((attr) => {
							if (draft[attr].length === 0) {
								delete draft[attr];
							}
						});
					});

					const valueOptions = condition.attr ? (filteredOptions[condition.attr] ?? []) : [];

					return (
						<Grid gridColumn="1 / span 4" templateColumns="subgrid" key={index}>
							<Grid
								gridColumn="1 / span 3"
								templateColumns="subgrid"
								border="1px solid"
								borderColor={error ? 'error.inputBorder' : 'border.input'}
								borderRadius="md"
								{...(!!error && {
									boxShadow: '0 0 0 1px #d82c2fff',
								})}
							>
								<Dropdown
									onChange={(value) => handleChangeAttr(index, value)}
									placeholder={t('audienceAttrPlaceholder')}
									renderOption={(value) => t('audienceLabel', { context: value, fallback: value.replaceAll('_', ' ') })}
									value={condition.attr}
									matchWidth
								>
									<DropdownInput border="none" _active={{ border: 'none' }} w="full" textTransform="capitalize" />
									<DropdownMenu>
										{Object.keys(filteredOptions).map((attr) => (
											<DropdownOption key={attr} value={attr} textTransform="capitalize">
												{t('audienceLabel', { context: attr, fallback: attr.replaceAll('_', ' ') })}
											</DropdownOption>
										))}
									</DropdownMenu>
								</Dropdown>

								<HStack p={2} borderInline="1px solid" borderColor={error ? 'error.inputBorder' : 'border.input'}>
									<Text>{t('audienceEq')}</Text>
								</HStack>

								<Conceal if={condition.attr === 'has_reports?'}>
									<Reveal if={condition.value}>
										<HStack px={3} py={2} minW={0}>
											<Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" flexGrow={1} flexBasis={0}>
												{valueOptions.find((o) => o.key === condition.value)?.name}
											</Box>
											<Button
												aria-label={t('clearCondition')}
												bg="none"
												boxSize={4}
												isDisabled={isDisabled}
												minW={0}
												px={0}
												onClick={() => handleChangeValue(index, '')}
												_hover={{ bg: 'none' }}
												color="text.default"
											>
												<XOutline size="xs" />
											</Button>
										</HStack>
									</Reveal>

									<Conceal if={condition.value}>
										<AttributeSearch
											isDisabled={isDisabled || !condition.attr}
											noResultsMessage={t('searchNoResults')}
											onSelect={(value) => handleChangeValue(index, value)}
											options={valueOptions}
											placeholder={!condition.attr ? undefined : t('searchPlaceholder', { context: placeholderContext(condition.attr) })}
										/>
									</Conceal>
								</Conceal>

								<Reveal if={condition.attr === 'has_reports?'}>
									<Dropdown
										onChange={(value) => handleChangeValue(index, value)}
										placeholder={t('audienceValuePlaceholder')}
										value={condition.value}
										renderOption={(value) => valueOptions.find((o) => o.key === value)?.name}
									>
										<DropdownInput border="none" _active={{ border: 'none' }} w="full" />
										<DropdownMenu>
											{valueOptions.map((val) => (
												<DropdownOption key={val.key} value={val.key}>
													{val.name}
												</DropdownOption>
											))}
										</DropdownMenu>
									</Dropdown>
								</Reveal>
							</Grid>

							<Flex justify="flex-end">
								<IconButton aria-label={t('removeCondition')} isDisabled={isDisabled} isRound onClick={() => handleRemove(index)}>
									<TrashOutline />
								</IconButton>
							</Flex>
						</Grid>
					);
				})}
			</Grid>

			<FormErrorMessage>{error}</FormErrorMessage>

			<HStack mt={2}>
				<Conceal if={!Object.keys(availableOptions).length}>
					<Button isDisabled={isDisabled} leftIcon={<PlusOutline size="sm" />} variant="ghost" onClick={handleAdd}>
						{t('audienceAdd')}
					</Button>
				</Conceal>
				<Spacer />
				<Button colorScheme="secondary" isDisabled={isDisabled} variant="ghost" onClick={handleClear}>
					{t('audienceClear')}
				</Button>
			</HStack>
		</Box>
	);
};
